import { useCallback, useRef, useState } from 'react';
import type { Action, AgentConfig, ContentBlock, HostContextItem, SSEEvent } from '@beidou/agent-protocol';
import type { MessageReasoning, ReasoningStep } from '../components/ReasoningPanel';
import {
  applyReasoningDelta,
  applyReasoningDone,
  emptyReasoning,
  stopReasoningStreaming,
} from '../utils/reasoningState';
import { isAbortError, resolveStatusLabel, FALLBACK_PROGRESS_LABEL } from '../utils/chatUi';
import type { AgentEngine } from '../utils/agentEngine';

export interface TurnUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  llm_calls?: number;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
  streaming?: boolean;
  markdownStreaming?: boolean;
  cancelled?: boolean;
  reasoning?: MessageReasoning;
  reasoningExpanded?: boolean;
  progressLabel?: string;
  progressPhase?: string;
  serverMessageId?: string;
  traceId?: string;
  feedbackRating?: 'up' | 'down';
  /** Resolved turn engine from done.meta.engine */
  turnEngine?: string;
  engineStatus?: string;
  /** LLM token usage from done.meta.usage */
  usage?: TurnUsage;
  /** Server-reported turn latency (ms) from done.meta.latency_ms */
  latencyMs?: number;
}

export interface SendOptions {
  regenerate?: boolean;
  truncateCount?: number;
  skipUserBubble?: boolean;
}

export interface UseChatOptions {
  engine?: AgentEngine | null;
  uiRole?: 'user' | 'dev' | 'ops' | null;
  /** 宿主详情页当前条目，随 chat 上报供 similar 等工具作种子 */
  contextItem?: HostContextItem | null;
  /** 宿主页当前内容类型，随 chat 上报作类型兜底；不参与 session 身份 */
  contextItemType?: string | null;
  onRefreshToken?: () => Promise<string | null>;
  onError?: (message: string, status?: number) => void;
  onRateLimited?: (retryAfterSec: number) => void;
}


function upsertSummaryStep(steps: ReasoningStep[], phase: string, label: string): ReasoningStep[] {
  const next = steps.filter((s) => s.phase !== phase);
  next.push({ phase, label });
  const order = ['plan', 'react', 'tool', 'observe', 'sql', 'critic', 'summary'];
  next.sort((a, b) => order.indexOf(a.phase) - order.indexOf(b.phase));
  return next;
}

export function useChat(apiUrl: string, token: string | null, options?: UseChatOptions) {
  const engine = options?.engine ?? null;
  const uiRole = options?.uiRole ?? null;
  const onRefreshToken = options?.onRefreshToken;
  const onError = options?.onError;
  const onRateLimited = options?.onRateLimited;
  const contextItemRef = useRef(options?.contextItem ?? null);
  contextItemRef.current = options?.contextItem ?? null;
  const contextItemTypeRef = useRef(options?.contextItemType ?? null);
  contextItemTypeRef.current = options?.contextItemType ?? null;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const stopGeneration = useCallback(() => {
    abortRef.current?.abort();
    setLoading(false);
    setStatus('');
  }, []);

  const runChat = useCallback(
    async (text: string, sendOptions?: SendOptions) => {
      if (!token || loading) return;
      const trimmed = text.trim();
      if (!trimmed && !sendOptions?.regenerate) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const userMsg: ChatMessage | null = sendOptions?.skipUserBubble
        ? null
        : { id: `u-${Date.now()}`, role: 'user', content: trimmed };
      const assistantId = `a-${Date.now()}`;
      const startedAt = Date.now();

      setMessages((prev) => {
        let base = prev;
        if (sendOptions?.truncateCount !== undefined) {
          base = prev.slice(0, sendOptions.truncateCount);
        } else if (sendOptions?.regenerate) {
          base = prev.slice(0, -1);
        }
        const next = [...base];
        if (userMsg) next.push(userMsg);
        next.push({
          id: assistantId,
          role: 'assistant',
          content: [],
          streaming: true,
          markdownStreaming: false,
          reasoning: emptyReasoning(),
          reasoningExpanded: false,
          progressLabel: FALLBACK_PROGRESS_LABEL,
          progressPhase: 'plan',
          turnEngine: engine ?? undefined,
        });
        return next;
      });
      setLoading(true);
      setStatus('');
      setSessionExpired(false);

      let blocks: ContentBlock[] = [];
      let reasoning: MessageReasoning = emptyReasoning();

      const body: Record<string, unknown> = { message: trimmed };
      if (sendOptions?.regenerate) body.regenerate = true;
      if (sendOptions?.truncateCount !== undefined) body.truncate_count = sendOptions.truncateCount;
      if (engine) body.engine = engine;
      if (uiRole) body.ui_role = uiRole;
      const hostCtxType = contextItemTypeRef.current;
      if (hostCtxType) body.context_item_type = hostCtxType;
      const hostCtx = contextItemRef.current;
      if (hostCtx?.item_id) {
        body.context_item = {
          item_id: hostCtx.item_id,
          ...(hostCtx.title ? { title: hostCtx.title } : {}),
          ...(hostCtx.item_type ? { item_type: hostCtx.item_type } : {}),
        };
      }

      const postChat = (authToken: string) =>
        fetch(`${apiUrl}/v1/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
            ...(engine ? { 'X-Agent-Engine': engine } : {}),
            ...(uiRole ? { 'X-UI-Role': uiRole } : {}),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

      const finalizeStopped = () => {
        const elapsedMs = Date.now() - startedAt;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  streaming: false,
                  markdownStreaming: false,
                  cancelled: true,
                  content: blocks.filter(Boolean).length
                    ? blocks.filter(Boolean)
                    : [{ type: 'markdown', text: '已停止生成。' }],
                  progressLabel: undefined,
                  progressPhase: undefined,
                  reasoning: stopReasoningStreaming({
                    ...reasoning,
                    summaryLine: '已停止',
                    elapsedMs,
                  }),
                }
              : m
          )
        );
      };

      try {
        let authToken = token;
        let resp = await postChat(authToken);
        if (resp.status === 401 && onRefreshToken) {
          const refreshed = await onRefreshToken();
          if (refreshed) {
            authToken = refreshed;
            resp = await postChat(authToken);
          }
        }

        if (resp.status === 429) {
          const retryAfter = Number(resp.headers.get('Retry-After') || 30);
          onRateLimited?.(retryAfter);
          throw new Error(`请求过于频繁，请 ${retryAfter} 秒后重试`);
        }

        if (!resp.ok) {
          if (resp.status === 401) setSessionExpired(true);
          let detail = `HTTP ${resp.status}`;
          try {
            const err = await resp.json();
            if (typeof err.detail === 'string') detail = err.detail;
          } catch {
            /* ignore */
          }
          onError?.(detail, resp.status);
          throw new Error(detail);
        }

        const reader = resp.body?.getReader();
        if (!reader) throw new Error('No stream');

        const decoder = new TextDecoder();
        let buffer = '';
        const markdownTexts: string[] = [];
        blocks = [];
        reasoning = emptyReasoning();
        let implicitRound = 0;
        let turnUsage: ChatMessage['usage'];

        const patchAssistant = (patch: Partial<ChatMessage>) => {
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m;
              const next: ChatMessage = { ...m, ...patch };
              const emptyContent =
                !next.content ||
                (Array.isArray(next.content) && next.content.length === 0);
              if (next.streaming && emptyContent && !next.progressLabel) {
                next.progressLabel = FALLBACK_PROGRESS_LABEL;
              }
              return next;
            })
          );
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const raw = line.slice(5).trim();
            if (!raw) continue;
            let event: SSEEvent;
            try {
              event = JSON.parse(raw);
            } catch {
              continue;
            }

            if (event.type === 'status') {
              patchAssistant({
                progressLabel: resolveStatusLabel(event.status, event.tool, event.label),
                progressPhase: event.phase || event.status,
              });
            } else if (event.type === 'reasoning_summary') {
              const steps = upsertSummaryStep(reasoning.summarySteps ?? [], event.phase, event.text);
              reasoning = {
                ...reasoning,
                summarySteps: steps,
                summaryLine: steps.map((s) => s.label).join(' · '),
              };
              patchAssistant({ reasoning: { ...reasoning }, reasoningExpanded: true });
            } else if (event.type === 'reasoning_delta') {
              const applied = applyReasoningDelta(
                reasoning,
                event.phase,
                event.delta,
                event.round,
                implicitRound,
                event.layer
              );
              reasoning = applied.reasoning;
              implicitRound = applied.implicitRound;
              patchAssistant({ reasoning: { ...reasoning }, reasoningExpanded: true });
            } else if (event.type === 'reasoning_done') {
              reasoning = applyReasoningDone(reasoning, event.phase, event.round);
              patchAssistant({ reasoning: { ...reasoning } });
            } else if (event.type === 'block') {
              blocks[event.block_index] = event.block;
              patchAssistant({ content: [...blocks.filter(Boolean)], markdownStreaming: false });
            } else if (event.type === 'block_delta') {
              markdownTexts[event.block_index] = (markdownTexts[event.block_index] || '') + event.delta;
              const tempBlocks = [...blocks];
              tempBlocks[event.block_index] = { type: 'markdown', text: markdownTexts[event.block_index] };
              patchAssistant({ content: tempBlocks.filter(Boolean), markdownStreaming: true });
            } else if (event.type === 'cancelled') {
              finalizeStopped();
              return;
            } else if (event.type === 'done') {
              const meta = event.meta || {};
              const elapsedMs =
                typeof meta.reasoning_elapsed_ms === 'number'
                  ? meta.reasoning_elapsed_ms
                  : Date.now() - startedAt;
              const summaryFromMeta =
                typeof meta.reasoning_summary === 'string' ? meta.reasoning_summary : undefined;
              if (summaryFromMeta) reasoning = { ...reasoning, summaryLine: summaryFromMeta };
              reasoning = { ...reasoning, elapsedMs };
              const usageRaw = meta.usage as Record<string, unknown> | undefined;
              let usage =
                usageRaw && typeof usageRaw === 'object'
                  ? {
                      prompt_tokens: Number(usageRaw.prompt_tokens) || 0,
                      completion_tokens: Number(usageRaw.completion_tokens) || 0,
                      total_tokens: Number(usageRaw.total_tokens) || 0,
                      llm_calls:
                        typeof usageRaw.llm_calls === 'number' ? usageRaw.llm_calls : undefined,
                    }
                  : undefined;
              if (
                !usage &&
                Array.isArray(meta.llm_usage_records) &&
                meta.llm_usage_records.length > 0
              ) {
                let prompt = 0;
                let completion = 0;
                let total = 0;
                for (const row of meta.llm_usage_records) {
                  if (!row || typeof row !== 'object') continue;
                  const rec = row as Record<string, unknown>;
                  prompt += Number(rec.prompt_tokens) || 0;
                  completion += Number(rec.completion_tokens) || 0;
                  total += Number(rec.total_tokens) || 0;
                }
                if (total <= 0) total = prompt + completion;
                if (total > 0 || prompt > 0 || completion > 0) {
                  usage = {
                    prompt_tokens: prompt,
                    completion_tokens: completion,
                    total_tokens: total,
                    llm_calls: meta.llm_usage_records.length,
                  };
                }
              }
              const hasUsage =
                usage &&
                (usage.total_tokens > 0 || usage.prompt_tokens > 0 || usage.completion_tokens > 0);
              turnUsage = hasUsage ? usage : undefined;
              patchAssistant({
                reasoning: { ...reasoning },
                progressLabel: undefined,
                progressPhase: undefined,
                markdownStreaming: false,
                serverMessageId: event.message_id,
                traceId: typeof meta.trace_id === 'string' ? meta.trace_id : undefined,
                turnEngine: typeof meta.engine === 'string' ? meta.engine : undefined,
                usage: turnUsage,
                latencyMs: typeof meta.latency_ms === 'number' ? meta.latency_ms : undefined,
                engineStatus: (() => {
                  const shadow = meta.shadow as
                    | { react_status?: string; compare_trace_id?: string }
                    | undefined;
                  if (shadow?.compare_trace_id) {
                    const st = shadow.react_status || 'pending';
                    return `shadow ${st} · ${shadow.compare_trace_id.slice(0, 8)}`;
                  }
                  return typeof meta.engine_status === 'string' ? meta.engine_status : undefined;
                })(),
              });
            }
          }
        }

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  streaming: false,
                  markdownStreaming: false,
                  content: blocks.filter(Boolean),
                  progressLabel: undefined,
                  progressPhase: undefined,
                  reasoning: stopReasoningStreaming({
                    ...reasoning,
                    elapsedMs: reasoning.elapsedMs ?? Date.now() - startedAt,
                  }),
                  usage: m.usage ?? turnUsage,
                  latencyMs: m.latencyMs,
                }
              : m
          )
        );
      } catch (e) {
        if (isAbortError(e)) {
          finalizeStopped();
          return;
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  streaming: false,
                  markdownStreaming: false,
                  progressLabel: undefined,
                  content: [{ type: 'markdown', text: `出错了：${e instanceof Error ? e.message : '未知错误'}` }],
                }
              : m
          )
        );
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setLoading(false);
        setStatus('');
      }
    },
    [apiUrl, token, loading, engine, uiRole, onRefreshToken, onError, onRateLimited]
  );

  const sendMessage = useCallback(
    (text: string, opts?: SendOptions) => runChat(text, opts),
    [runChat]
  );

  const regenerateLast = useCallback(() => {
    runChat('', { regenerate: true, skipUserBubble: true });
  }, [runChat]);

  const editAndResend = useCallback(
    (messageId: string, newText: string) => {
      const idx = messages.findIndex((m) => m.id === messageId);
      if (idx < 0) return;
      runChat(newText, { truncateCount: idx, skipUserBubble: false });
    },
    [messages, runChat]
  );

  const showWelcome = useCallback((config: AgentConfig) => {
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: [{ type: 'markdown', text: config.welcome_message || config.theme.welcome_message }],
      },
    ]);
  }, []);

  const loadHistory = useCallback((history: ChatMessage[]) => {
    if (history.length > 0) setMessages(history);
  }, []);

  const toggleReasoning = useCallback((messageId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, reasoningExpanded: !m.reasoningExpanded } : m))
    );
  }, []);

  const setFeedbackRating = useCallback((messageId: string, rating: 'up' | 'down') => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, feedbackRating: rating } : m))
    );
  }, []);

  const resetConversation = useCallback(async (): Promise<boolean> => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
    setStatus('');
    if (!token) return false;
    try {
      const resp = await fetch(`${apiUrl}/v1/session/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      return resp.ok;
    } catch {
      return false;
    }
  }, [apiUrl, token]);

  return {
    messages,
    loading,
    status,
    sessionExpired,
    setSessionExpired,
    sendMessage,
    regenerateLast,
    editAndResend,
    stopGeneration,
    showWelcome,
    loadHistory,
    setMessages,
    toggleReasoning,
    setFeedbackRating,
    resetConversation,
  };
}

export function handleAction(action: Action, sendMessage: (t: string, o?: SendOptions) => void) {
  if (action.type === 'send_message') {
    const payload = action.payload;
    const text =
      typeof payload === 'string'
        ? payload
        : payload && typeof payload === 'object' && 'message' in payload
          ? String((payload as { message?: unknown }).message || '')
          : String(payload || '');
    sendMessage(text);
    return;
  }
  if (action.type === 'open_item' || action.type === 'open_url' || action.type === 'custom') {
    window.parent.postMessage({ channel: 'beidou-agent', type: 'action', action }, '*');
  }
}
